import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MessageSquare, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import FeedbackThread from '../../components/feedback/FeedbackThread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantAdminFeedback = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState({ status: '', category: '' });
  const [openThreadId, setOpenThreadId] = useState(null);

  useEffect(() => {
    if (expanded) fetchFeedback();
  }, [expanded]);

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/feedback`);
      setItems(res.data.feedback || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleToggleThread = async (id) => {
    if (openThreadId === id) {
      setOpenThreadId(null);
      return;
    }
    setOpenThreadId(id);
    try {
      await axios.put(`${API_URL}/api/accountant/feedback/${id}/mark-read`);
      // optimistically clear unread flag
      setItems(prev => prev.map(it => it.id === id ? { ...it, unread_for_me: false } : it));
    } catch {
      // ignore
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/accountant/feedback/${id}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      fetchFeedback();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const filtered = items.filter(fb => {
    if (filter.status && fb.status !== filter.status) return false;
    if (filter.category && fb.category !== filter.category) return false;
    return true;
  });
  const openCount = items.filter(fb => fb.status === 'open').length;
  const unreadCount = items.filter(fb => fb.unread_for_me).length;

  const sentimentColor = { positive: 'text-emerald-400', negative: 'text-red-400', neutral: 'text-slate-400' };
  const statusBadge = { open: 'bg-amber-500/15 text-amber-400', reviewed: 'bg-sky-500/15 text-sky-400', resolved: 'bg-emerald-500/15 text-emerald-400' };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <div className="p-4 cursor-pointer flex items-center justify-between" onClick={() => setExpanded(!expanded)} data-testid="acct-admin-fb-toggle">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-sky-400" />
          <h2 className="text-base font-semibold text-white">Accountant Feedback</h2>
          {openCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-400 text-xs font-medium">{openCount} open</span>
          )}
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium" data-testid="acct-admin-fb-unread">
              {unreadCount} new reply
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </div>

      {expanded && (
        <CardContent className="pt-0 pb-4 px-4">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-sky-400" /></div>
          ) : (
            <>
              {/* Filters */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['', 'open', 'reviewed', 'resolved'].map(s => (
                  <button key={s} onClick={() => setFilter(p => ({ ...p, status: s }))}
                    className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                      filter.status === s ? 'bg-sky-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}>{s || 'All'}</button>
                ))}
              </div>

              {filtered.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No accountant feedback yet.</p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filtered.map(fb => {
                    const isOpenThread = openThreadId === fb.id;
                    return (
                      <div key={fb.id} className="p-3 bg-slate-700/30 rounded-lg border border-slate-600" data-testid={`acct-fb-${fb.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-medium text-white">{fb.user_name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusBadge[fb.status] || ''}`}>{fb.status}</span>
                              <span className={`text-[10px] ${sentimentColor[fb.sentiment] || ''}`}>{fb.sentiment}</span>
                              <span className="text-[10px] text-slate-600">{fb.category?.replace('_', ' ')}</span>
                              {fb.unread_for_me && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-amber-400 bg-amber-500/15 border-amber-500/40 font-medium">
                                  New reply
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-600 mt-1">
                              {new Date(fb.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              {fb.user_email && ` · ${fb.user_email}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => handleToggleThread(fb.id)}
                              className="text-sky-400 hover:bg-sky-500/10 h-6 px-2 text-[10px]" data-testid={`acct-fb-toggle-${fb.id}`}>
                              {isOpenThread ? 'Close' : 'Open conversation'}
                            </Button>
                            {fb.status !== 'resolved' && (
                              <Button size="sm" variant="ghost" onClick={() => handleStatus(fb.id, 'resolved')}
                                className="text-emerald-400 hover:bg-emerald-500/10 h-6 px-2 text-[10px]">Resolve</Button>
                            )}
                          </div>
                        </div>
                        {isOpenThread && (
                          <div className="mt-3">
                            <FeedbackThread
                              feedbackId={fb.id}
                              thread={fb.thread || []}
                              viewerRole="admin"
                              endpoint={`${API_URL}/api/accountant/feedback/${fb.id}/message`}
                              onMessageSent={fetchFeedback}
                              compact
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AccountantAdminFeedback;
